import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { cacheCommands, getCachedCommands } from "@/lib/pdv-cache";
import { notificationSound } from "@/lib/notification-sound";
import { getErrorMessage, logError } from "@/lib/error-messages";
import { sortByNumberField } from "@/lib/utils";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import { getCommands } from "@/lib/dataGateway";

export type CommandStatus = "available" | "in_use" | "grouped";

export interface Command {
  id: string;
  restaurant_id: string;
  number: string;
  is_active: boolean;
  status: CommandStatus;
  current_order_id: string | null;
  grouped_to_order_id: string | null;
  customer_name: string | null;
  service_tax_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function useCommands() {
  const { activeRestaurant } = useRestaurantContext();
  const [commands, setCommands] = useState<Command[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const previousCommandsRef = useRef<Command[]>([]);
  const { logOperation } = useOperationLogs();

  const fetchCommands = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Try to fetch from network first (via DataGateway: local server -> Supabase)
      if (navigator.onLine) {
        const { data, error, source } = await getCommands<Command>(activeRestaurant.id);
        if (import.meta.env.DEV && !error) {
          console.debug(`[useCommands] fonte: ${source}`);
        }

        if (error) {
          // Network error - try cache
          const cached = await getCachedCommands<Command>(activeRestaurant.id);
          if (cached) {
            setCommands(cached);
            toast.info("Usando comandas do cache offline");
          } else {
            toast.error("Erro ao carregar comandas");
          }
        } else {
          const commandsData = sortByNumberField(data as Command[]);
          setCommands(commandsData);
          previousCommandsRef.current = commandsData;
          // Cache for offline use (fire-and-forget to prevent blocking)
          cacheCommands(activeRestaurant.id, data || []).catch(() => {});
        }
      } else {
        // Offline mode - use cache
        const cached = await getCachedCommands<Command>(activeRestaurant.id);
        if (cached) {
          setCommands(cached);
        } else {
          toast.error("Sem conexão e sem cache de comandas");
        }
      }
    } catch (error) {
      // Ensure we never get stuck in loading state
      console.error("Error fetching commands:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant]);

  const createCommand = async (data: { number: string }) => {
    if (!activeRestaurant) return null;

    try {
      const { data: newCommand, error } = await supabase
        .from("commands")
        .insert({
          restaurant_id: activeRestaurant.id,
          number: data.number,
        })
        .select()
        .single();

      if (error) throw error;

      await logOperation("create", "command", newCommand.id, {
        number: newCommand.number,
      });

      setCommands((prev) => [...prev, newCommand as Command]);
      toast.success("Comanda criada com sucesso!");
      return newCommand;
    } catch (error: any) {
      logError(error, "useCommands.createCommand");
      toast.error(getErrorMessage(error, "criar", "command"));
      return null;
    }
  };

  const createBatchCommands = async (startNumber: number, endNumber: number) => {
    if (!activeRestaurant) return null;

    try {
      const commandsToCreate = [];
      for (let i = startNumber; i <= endNumber; i++) {
        commandsToCreate.push({
          restaurant_id: activeRestaurant.id,
          number: String(i).padStart(3, "0"),
        });
      }

      const { data, error } = await supabase
        .from("commands")
        .insert(commandsToCreate)
        .select();

      if (error) throw error;

      await logOperation("create", "command", undefined, {
        batch: true,
        startNumber,
        endNumber,
        count: commandsToCreate.length,
      });

      await fetchCommands();
      toast.success(`${commandsToCreate.length} comandas criadas com sucesso!`);
      return data;
    } catch (error: any) {
      logError(error, "useCommands.createBatchCommands");
      toast.error(getErrorMessage(error, "criar", "command"));
      return null;
    }
  };

  const updateCommand = async (
    id: string,
    data: Partial<Pick<Command, "number" | "is_active" | "status" | "customer_name" | "service_tax_enabled">>
  ) => {
    const currentCommand = commands.find(c => c.id === id);
    
    try {
      const { data: updated, error } = await supabase
        .from("commands")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Log status changes as open/close
      if (data.status && currentCommand?.status !== data.status) {
        if (data.status === "in_use") {
          await logOperation("open", "command", id, {
            number: currentCommand?.number,
            customer_name: data.customer_name,
          });
        } else if (data.status === "available") {
          await logOperation("close", "command", id, {
            number: currentCommand?.number,
          });
        }
      } else {
        await logOperation("update", "command", id, {
          number: updated.number,
          changes: data,
        });
      }

      setCommands((prev) =>
        prev.map((c) => (c.id === id ? (updated as Command) : c))
      );
      toast.success("Comanda atualizada!");
      return updated;
    } catch (error: any) {
      logError(error, "useCommands.updateCommand");
      toast.error(getErrorMessage(error, "atualizar", "command"));
      return null;
    }
  };

  const deleteCommand = async (id: string) => {
    const command = commands.find(c => c.id === id);
    
    try {
      const { error } = await supabase.from("commands").delete().eq("id", id);

      if (error) throw error;

      await logOperation("delete", "command", id, {
        number: command?.number,
      });

      setCommands((prev) => prev.filter((c) => c.id !== id));
      toast.success("Comanda removida!");
      return true;
    } catch (error) {
      logError(error, "useCommands.deleteCommand");
      toast.error(getErrorMessage(error, "excluir", "command"));
      return false;
    }
  };

  const getActiveCommands = useCallback(() => {
    return commands.filter((c) => c.is_active);
  }, [commands]);

  const getAvailableCommands = useCallback(() => {
    return commands.filter((c) => c.is_active && c.status === "available");
  }, [commands]);

  useEffect(() => {
    fetchCommands();
  }, [fetchCommands]);

  // Real-time subscription
  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel(`commands-${activeRestaurant.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'commands',
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setCommands((prev) => sortByNumberField([...prev, payload.new as Command]));
          } else if (payload.eventType === 'UPDATE') {
            const newCommand = payload.new as Command;
            const oldCommand = previousCommandsRef.current.find(c => c.id === newCommand.id);
            
            // Play sound if status changed
            if (oldCommand && oldCommand.status !== newCommand.status) {
              notificationSound.playCommandStatusChange();
            }
            
            setCommands((prev) => {
              const updated = sortByNumberField(prev.map((c) =>
                c.id === payload.new.id ? newCommand : c
              ));
              previousCommandsRef.current = updated;
              return updated;
            });
          } else if (payload.eventType === 'DELETE') {
            setCommands((prev) => prev.filter((c) => c.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [activeRestaurant]);

  return {
    commands,
    isLoading,
    fetchCommands,
    createCommand,
    createBatchCommands,
    updateCommand,
    deleteCommand,
    getActiveCommands,
    getAvailableCommands,
  };
}
